// Tuning constants — the design knobs from game-design-doc §19.
//
// All values are set (no nulls): these are the v1 playtest baseline.
// Tune by playing, not on paper. Keep units in comments.

export const tuning = {
  // ── Speed ──────────────────────────────────────────────────────────────
  /** Minimum player speed (edges/second). A calm, readable pace. */
  minSpeed: 2.0,
  /**
   * Reference speed (edges/second) — NOT a cap (player request 2026-06-12:
   * speed is unbounded; passive decay is the only counterweight). Used to
   * normalize the decay slope and the audio pitch ramp, and as the per-pass
   * tunneling-safe speed for tick sub-stepping.
   */
  maxSpeed: 6.0,
  /** Speed above which passive decay kicks in (60% of maxSpeed). */
  decayThreshold: 3.6,
  /** Speed shed per second at maxSpeed (edges/second). Softened 2026-06-12. */
  decayRate: 0.6,

  // ── Pellets ────────────────────────────────────────────────────────────
  /** Speed added per pellet (edges/second). 8 pellets: min → max. */
  pelletSpeedGain: 0.5,
  /** Health added per pellet. 10 pellets: 0 → maxHealth. */
  pelletHealthGain: 10,
  /** Seconds from pellet consumption to reappearance elsewhere. Faster economy (2026-06-12). */
  pelletRespawnSeconds: 2.5,
  /**
   * Pellet-effect taper (player request 2026-06-12): below taperStart a
   * pellet's full gain goes to speed; the speed share fades linearly to zero
   * at taperEnd, and the remainder accrues as FUEL instead.
   */
  speedTaperStart: 6.0,
  speedTaperEnd: 10.0,
  /**
   * Fuel cap (speed units). Passive decay burns fuel BEFORE touching speed,
   * so accumulated pace is preserved while the tank lasts.
   */
  maxFuel: 6.0,
  /** Target pellet count per 100 edges. Raised for a friendlier start (2026-06-12). */
  pelletDensity: 16,

  // ── Health / combat ────────────────────────────────────────────────────
  /** Maximum player health (ring renders as a percentage). */
  maxHealth: 100,
  /**
   * Flat health damage to EACH player in a player-player collision.
   * At 18, a KO takes 6 clean hits — bumps matter, ramming is a commitment.
   */
  playerCollisionDamage: 18,
  /** Speed shed by EACH player in a player-player collision (edges/second). */
  playerCollisionSpeedLoss: 1.0,

  // ── Entity placement & contact (player request 2026-06-12) ────────────
  /**
   * Pellets and bouncers sit near edge centers: |t − 0.5| ≤ this value.
   * Keeps every object visibly far from junctions, so same-edge contact
   * checks are unambiguous and jitter-free.
   */
  entityMidEdgeOffset: 0.15,
  /** Cooldown after a bounce so the reversal can't oscillate (seconds). */
  bounceCooldownSeconds: 0.5,
  /** Passive decay freeze after eating a pellet (seconds; player request). */
  decayFreezeSeconds: 1.0,

  // ── Invulnerability windows ────────────────────────────────────────────
  /** Respawn protection (seconds). Visible as a pulsing ring. */
  respawnInvulSeconds: 2.0,
  /** Mutual i-frames after a player-player hit (seconds). */
  collisionIframeSeconds: 0.5,

  // ── World ──────────────────────────────────────────────────────────────
  /** Bouncer count per 100 edges. */
  bouncerDensity: 5,
  /** Default world size (hex cells). 16×12 cells = 576 edges, 384 vertices. */
  worldCols: 16,
  worldRows: 12,
  /** Respawn clearance: min graph-distance (in edges) from bouncers/players. */
  minRespawnClearanceEdges: 2,

  // ── Multiplayer ────────────────────────────────────────────────────────
  /** Fixed simulation rate (Hz). Server-authoritative. */
  tickHz: 30,
  /** Snapshot broadcast rate (Hz) — every 2nd tick. */
  broadcastHz: 15,
  /** Disconnect grace: dot persists stationary this long (seconds). */
  reconnectWindowSeconds: 10,
  /** Room capacity. */
  maxPlayersPerRoom: 16,
} as const;

export type Tuning = typeof tuning;
